import { LockOutlined, UnlockOutlined, WarningFilled } from '@ant-design/icons'
import { Button, Col, Form, Input, InputNumber, Row } from 'antd'
import { Gutter } from 'antd/es/grid/row'
import TextArea from 'antd/es/input/TextArea'
import React, { FunctionComponent, useMemo } from 'react'

import { BUYBACK_FIELD as FIELD } from '../../../common/buybackTypes'
import { PROPERTY_TYPES } from '../../../common/drmTypes'
import { inputNumberFormatter } from '../../utils/buybackHelpers'
import { useToggleState } from '../../utils/useToggleState'

const gutter: Gutter | [Gutter, Gutter] = [16, 16]

export interface BuybackFormCustomizationFragmentProps {
  propertyType: PROPERTY_TYPES
}

export const BuybackFormCustomizationFragment: FunctionComponent<BuybackFormCustomizationFragmentProps> =
  ({ propertyType }) => {
    const offerLimit =
      Form.useWatch(FIELD.OfferPercentage) > 60 ? 'warning' : undefined
    const interestLimit =
      Form.useWatch(FIELD.AnnualInterestRate) < 10 ? 'warning' : undefined
    const localRent = Form.useWatch(FIELD.RentLocalPrice)
    const baseRent = Form.useWatch(FIELD.RentBasePrice)

    const baseRentTitle = useMemo(() => {
      if (!localRent || !baseRent) return 'Základní nájemné'

      const adventage = Math.round(((localRent - baseRent) / localRent) * 100)
      if (adventage < 20) return 'Základní nájemné'

      return (
        <span>
          Základní nájemné{' '}
          <span className="text-green-600">↓ {adventage} % oproti trhu</span>
        </span>
      )
    }, [localRent, baseRent])

    const [hasDiscount, toggleDiscount] = useToggleState(false)

    return (
      <Row gutter={gutter} className="mt-4">
        <Col xs={24} md={12}>
          <Form.Item
            label="Cenová nabídka výkupu nemovitosti"
            validateStatus={offerLimit}
          >
            <div className="flex space-x-4">
              <Form.Item
                name={FIELD.OfferPercentage}
                validateStatus={offerLimit}
                className="w-1/2 !mb-0 !bg-gray-50"
              >
                <InputNumber
                  min={55}
                  step={0.1}
                  className="w-full"
                  addonAfter="%"
                  addonBefore={offerLimit ? <WarningFilled /> : undefined}
                  disabled={!hasDiscount}
                />
              </Form.Item>

              <Form.Item
                name={FIELD.OfferPrice}
                validateStatus={offerLimit}
                className="w-1/2 !mb-0 !bg-gray-50"
              >
                <InputNumber
                  min={0}
                  step={100_000}
                  className="w-full"
                  formatter={inputNumberFormatter}
                  addonAfter="Kč"
                  disabled
                />
              </Form.Item>
            </div>
          </Form.Item>
        </Col>

        <Col xs={20} md={11}>
          <Form.Item
            label="Roční navýšení hodnoty nemovitosti"
            validateStatus={offerLimit}
          >
            <div className="flex space-x-4">
              <Form.Item
                name={FIELD.AnnualInterestRate}
                validateStatus={interestLimit}
                className="w-1/2 !mb-0 !bg-gray-50"
              >
                <InputNumber
                  min={0}
                  step={0.1}
                  className="w-full"
                  addonAfter="%"
                  addonBefore={interestLimit ? <WarningFilled /> : undefined}
                  disabled={!hasDiscount}
                />
              </Form.Item>

              <Form.Item
                name={FIELD.AnnualInterestPrice}
                validateStatus={interestLimit}
                className="w-1/2 !mb-0 !bg-gray-50"
              >
                <InputNumber
                  min={0}
                  step={100_000}
                  className="w-full"
                  formatter={inputNumberFormatter}
                  addonAfter="Kč"
                  disabled
                />
              </Form.Item>
            </div>
          </Form.Item>
        </Col>

        <Col xs={4} md={1}>
          <Form.Item label="&nbsp;">
            <Button
              title="Individuální sleva"
              icon={hasDiscount ? <LockOutlined /> : <UnlockOutlined />}
              onClick={toggleDiscount}
            />
          </Form.Item>
        </Col>

        {(interestLimit || offerLimit) && (
          <Col xs={24} md={24}>
            <Form.Item
              name={FIELD.OfferNote}
              validateStatus="warning"
              label={
                <span className="text-yellow-500">
                  Překročen povolený rozsah - nabídka podléhá schvalovacímu
                  procesu
                </span>
              }
            >
              <TextArea className="w-full" placeholder="Uveďte vysvětlení" />
            </Form.Item>
          </Col>
        )}

        <Col xs={24} md={12}>
          {propertyType === PROPERTY_TYPES.UNIT && (
            <Form.Item
              name={FIELD.RentLocalPrice}
              label="Tržní nájemné v lokalitě"
            >
              <InputNumber
                disabled
                className="w-full"
                formatter={inputNumberFormatter}
                addonAfter="Kč"
              />
            </Form.Item>
          )}
        </Col>

        <Col xs={24} md={12}>
          <Form.Item name={FIELD.RentBasePrice} label={baseRentTitle}>
            <InputNumber
              disabled
              className="w-full !bg-gray-50"
              formatter={inputNumberFormatter}
              addonAfter="Kč"
            />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item name={FIELD.RentalFeesPrice} label="Služby a energie">
            <InputNumber
              disabled
              className="w-full !bg-gray-50"
              formatter={inputNumberFormatter}
              addonAfter="Kč"
            />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item
            name={FIELD.RentTotalPrice}
            label="Nájemné včetně služeb a energií"
          >
            <InputNumber
              disabled
              className="w-full !bg-gray-50"
              formatter={inputNumberFormatter}
              addonAfter="Kč"
            />
          </Form.Item>
        </Col>

        <Col xs={24} md={8}>
          <Form.Item name={FIELD.RentDurationMonths} label="Délka nájmu">
            <InputNumber
              min={0}
              step={1}
              max={120}
              className="w-full"
              addonAfter="měs."
            />
          </Form.Item>
        </Col>

        <Col xs={24} md={8}>
          <Form.Item name={FIELD.RentPrice} label="Sjednaná výše nájemného">
            <InputNumber
              min={0}
              step={100}
              className="w-full"
              formatter={inputNumberFormatter}
              addonAfter="Kč"
            />
          </Form.Item>
        </Col>

        <Col xs={24} md={8}>
          <Form.Item
            name={FIELD.BuybackPrice}
            label="Garant. výše zpětného odkupu"
          >
            <InputNumber
              min={0}
              step={1000}
              formatter={inputNumberFormatter}
              addonAfter="Kč"
              className="w-full"
            />
          </Form.Item>

          {/* Hidden fields for Form.useWatch */}
          <Form.Item name={FIELD.BaseBuybackPrice} hidden>
            <InputNumber />
          </Form.Item>
          <Form.Item name={FIELD.PropertyPrice} hidden>
            <InputNumber />
          </Form.Item>
          <Form.Item name={FIELD.MarketPriceAnnualGrowth} hidden>
            <InputNumber />
          </Form.Item>
          <Form.Item name={FIELD.MarketSaleCosts} hidden>
            <InputNumber />
          </Form.Item>
          <Form.Item name={FIELD.MarketSaleDurationMonths} hidden>
            <InputNumber />
          </Form.Item>
          <Form.Item name={FIELD.InvestorRate} hidden>
            <InputNumber />
          </Form.Item>
        </Col>

        <Col xs={24} md={24}>
          <Form.Item name={FIELD.Note} label="Doplňující poznámky">
            <Input.TextArea className="w-full" />
          </Form.Item>
        </Col>
      </Row>
    )
  }
